import { useState } from 'react';

import { Icon, type IconName } from '../components/Icon.js';
import { ctaModeOf } from '../data/cta.js';
import type {
  CtaButton,
  CtaButtonMode,
  CtaPayload,
  Message,
  MessageId,
} from '../fixtures/domain.js';
import { useMessageCta } from './useMessageCta.js';
import styles from './MessageCtaButtons.module.css';

interface MessageCtaSlotProps {
  message: Message;
  currentUserId: string;
  sendReply: (text: string, quotedMessageId: MessageId, plain?: boolean) => Promise<void>;
}

/**
 * The stateful half of the row: it owns `useMessageCta` so the hook is mounted
 * only for messages that actually carry buttons.
 *
 * That is the entire reason this component exists. If the bubble called the hook
 * itself, every plain sentence in a thread would pay for a pending state, two
 * effects and a bus subscription it can never use — a hundred-message thread
 * paying a hundred times for a feature five messages have.
 */
export function MessageCtaSlot({ message, currentUserId, sendReply }: MessageCtaSlotProps) {
  const { cta, pendingActionId, disabled, notice, onButtonClick } = useMessageCta(
    message,
    currentUserId,
    sendReply,
  );
  if (!cta) return null;

  return (
    <>
      <MessageCtaButtons
        cta={cta}
        pendingActionId={pendingActionId}
        disabled={disabled}
        onClick={onButtonClick}
      />
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
    </>
  );
}

interface MessageCtaButtonsProps {
  cta: CtaPayload;
  /** Which button is mid-flight, if any. */
  pendingActionId: string | null;
  /** The whole block is untouchable. Its cause does not change how it looks. */
  disabled: boolean;
  onClick: (button: CtaButton) => void;
}

/**
 * Rows shown before the rest fold away, and rows shown once folding kicks in.
 *
 * Folding has to earn itself. Every hidden button costs the reader a choice they
 * cannot see, and the toggle occupies a row of its own, so hiding two buttons to
 * save one row is a straight loss. stream-chat folds above three because its
 * buttons are pills that wrap sideways, where a fourth can blow out the width.
 * These are stacked rows in a thread that already scrolls, so vertical space is
 * the cheap thing and the fold waits until it hides enough to be worth the tap.
 */
const INLINE_LIMIT = 4;
const FOLDED_VISIBLE = 3;

/**
 * The icon is derived from the mode, because the schema has no icon field and
 * because the thing worth warning someone about is not what a button means but
 * what it does the instant it is pressed:
 *
 *   url    something opens away from this thread
 *   reply  words appear in the thread, in your name, exactly as written
 *   url_reply  the same words appear, so it warns like reply
 *   event  nothing to warn about, so no icon
 */
const MODE_ICONS: Partial<Record<CtaButtonMode, IconName>> = {
  url: 'externalLink',
  reply: 'reply',
  url_reply: 'reply',
};

/**
 * The action block inside a bubble: a hairline, then one full-width row per
 * button, after WhatsApp's template buttons.
 *
 * Inside the bubble rather than under it, which is the point of the borrowed
 * pattern. A detached row of pills is chrome the app is offering; a block fused
 * to the bubble is part of what the sender said, and these buttons are the
 * sender's. It also settles the alignment question by not having one.
 *
 * Renders and nothing more. Every judgement about who may click and what a click
 * does was made before this got its props, which is why a disabled block has
 * exactly one look: spent, barred, ours to begin with, or waiting on a sibling
 * all answer "can I press this" the same way.
 */
function MessageCtaButtons({ cta, pendingActionId, disabled, onClick }: MessageCtaButtonsProps) {
  const [expanded, setExpanded] = useState(false);

  const interactive = !disabled && pendingActionId === null;
  // A block that went dead while it was open — a seal arrived, or a sibling was
  // clicked — folds back up. Derived rather than an effect resetting the state:
  // leaving a list of things nobody can press expanded is a tease, and there is
  // nothing to remember once it is untouchable.
  const open = expanded && interactive;

  const foldable = cta.buttons.length > INLINE_LIMIT;
  const visible = foldable && !open ? cta.buttons.slice(0, FOLDED_VISIBLE) : cta.buttons;
  const hiddenCount = cta.buttons.length - FOLDED_VISIBLE;

  return (
    <div className={styles.actions} role="group">
      {visible.map((button) => {
        const pending = pendingActionId === button.id;
        const chosen = disabled && cta.completedActionId === button.id;
        const faded = disabled ? !chosen : pendingActionId !== null && !pending;
        // The answer that was given, marked as given, whatever mode gave it. A
        // block of five identical greys does not say which one was pressed.
        const icon = chosen ? 'check' : MODE_ICONS[ctaModeOf(button)];

        return (
          <button
            key={button.id}
            type="button"
            className={[
              styles.action,
              styles[button.style ?? 'secondary'],
              faded ? styles.faded : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-disabled={!interactive}
            aria-busy={pending}
            // The whole label, for the one that wrapped past two lines.
            title={button.label}
            onClick={() => interactive && onClick(button)}
          >
            {pending && <span className={styles.spinner} aria-hidden="true" />}
            {!pending && icon && <Icon name={icon} size={16} />}
            <span className={styles.label}>{button.label}</span>
          </button>
        );
      })}

      {foldable && (
        <button
          type="button"
          className={`${styles.action} ${styles.more} ${interactive ? '' : styles.faded}`}
          aria-expanded={open}
          aria-disabled={!interactive}
          onClick={() => interactive && setExpanded((wasExpanded) => !wasExpanded)}
        >
          <span className={open ? styles.chevronOpen : styles.chevron}>
            <Icon name="chevronUp" size={16} />
          </span>
          {/* Counts what is hidden, not what exists. "See all 4 options" above
              two visible buttons leaves the reader working out whether four more
              are coming or two. */}
          <span className={styles.label}>{open ? 'Show less' : `Show ${hiddenCount} more`}</span>
        </button>
      )}
    </div>
  );
}
